import { useEffect } from 'react';

import { useOwnShareLink } from '@/hooks/useOwnShareLink';
import { setSharedLinkContent } from '@/modules/primum-shared-link';

/**
 * Keeps the device holder's link and its labels in the iOS app group, where the Messages and
 * Siri extensions read them. They run outside the JS runtime and cannot reach the session, so
 * the app hands the link over and takes it back. See `useOwnShareLink` for who gets a link and
 * why each guard exists.
 *
 * Cleared on sign-out, for any account with no link to share, and when an expert's link fails
 * to load, so a stale link or another role's wording is never left behind. Left alone while a
 * support session impersonates someone, so the device holder's own link survives the session,
 * and while an expert's link is still loading, so a relaunch does not blank it.
 */
export function useSyncSharedLink() {
  const { authenticatedUserId, actAsUserId, isAuthResolved, user, kind, shareLink, isLinkError } =
    useOwnShareLink();

  const url = shareLink?.url;

  const title = shareLink?.labels.title;

  const resultDialog = shareLink?.labels.resultDialog;

  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }

    if (!authenticatedUserId) {
      setSharedLinkContent(null);

      return;
    }

    if (actAsUserId) {
      return;
    }

    if (url && title && resultDialog) {
      setSharedLinkContent({ url, title, resultDialog });

      return;
    }

    if ((user && !kind) || isLinkError) {
      setSharedLinkContent(null);
    }
  }, [
    isAuthResolved,
    authenticatedUserId,
    actAsUserId,
    user,
    kind,
    url,
    title,
    resultDialog,
    isLinkError,
  ]);
}
